import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from 'lucide-react';

import BathroomImg from '@/assets/room-style/bathroom.png';
import ChangeStyleImg from '@/assets/style-settings/mode_ChangeStyle.webp';
import GoodWheaterImg from '@/assets/style-settings/mode_GoodWheater.webp';
import ImproveQualityImg from '@/assets/style-settings/mode_ImproveQuality.webp';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { ROOM_MODE_OPTIONS } from '../../constants/style-settings';

export interface RoomModeSelectionContentProps {
  roomMode?: ROOM_MODE_OPTIONS;
  handleUpdateRoomMode: (val?: ROOM_MODE_OPTIONS) => void;
}

const roomModeOptions = [
  { value: ROOM_MODE_OPTIONS.FURNISH_EMPTY_ROOM, image: BathroomImg },
  { value: ROOM_MODE_OPTIONS.CHANGE_STYLE, image: ChangeStyleImg },
  { value: ROOM_MODE_OPTIONS.FURNITURE_REMOVAL, image: BathroomImg },
  { value: ROOM_MODE_OPTIONS.CHANGE_FLOOR, image: BathroomImg },
  { value: ROOM_MODE_OPTIONS.CLEAN_ROOM, image: BathroomImg },
  { value: ROOM_MODE_OPTIONS.IMPROVE_IMAGE_QUALITY, image: ImproveQualityImg },
  { value: ROOM_MODE_OPTIONS.GOOD_WEATHER, image: GoodWheaterImg },
];

export const RoomModeSelectionContent = ({
  roomMode,
  handleUpdateRoomMode,
}: RoomModeSelectionContentProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>();

  const filteredRoomModeOptions = useMemo(
    () =>
      !search
        ? roomModeOptions
        : roomModeOptions.filter((option) =>
            t(option).toLowerCase().includes(search.toLowerCase())
          ),
    [t, search]
  );

  return (
    <div className="w-full">
      <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 lg:h-[60px]">
        <span className="text-sm font-semibold text-white">{t('mode')}</span>
      </div>
      <div className="bg-white p-2 sm:px-5 sm:py-6">
        <Input
          size="sm"
          className="border border-solid border-primary-light"
          placeholder={t('search')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          startIcon={<Search className="text-primary-light" />}
        />
        <div className="mt-3 grid max-h-calc-full-minus-100 grid-cols-2 gap-2 overflow-y-auto sm:max-h-[400px] lg:max-h-[472px]">
          {filteredRoomModeOptions.map((option, index) => {
            const isSelected = option.value === roomMode;

            return (
              <div
                key={`desired-style-option-${index}`}
                className={cn(
                  'relative flex cursor-pointer flex-col overflow-hidden rounded-sm border border-solid',
                  !isSelected ? 'border-text-primary' : 'border-primary-light'
                )}
                onClick={() => handleUpdateRoomMode(option.value)}
              >
                <div className="flex-1">
                  <img className="h-[84px] w-full object-cover" src={option.image} />
                </div>
                <div
                  className={cn(
                    'z-2 flex h-[30px] items-center justify-center',
                    isSelected ? 'bg-primary-light' : 'bg-text-primary'
                  )}
                >
                  <span className="text-center text-sm font-semibold leading-none text-white">
                    {t(option.value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
